import { FragmentChunk } from '@app/modules/fragments/types';
import { toTimestamp } from '@mi-front/core';

/**
 * Returns the index of the chunk that should appear active for the given absolute `currentTs`,
 * or null if the playhead is outside the duration timeline derived from chunks.
 *
 * Chunk `from`/`to` may not line up with playback time; we therefore convert to **relative** time:
 * subtract the first chunk's start (`chunks[0].from`), then test which **cumulative duration band**
 * contains that offset (e.g. 0–20s vs 20–60s when durations are 20s and 40s).
 */
export function getActiveChunkIndex(
  chunks: FragmentChunk[],
  currentTs: number | null,
): number | null {
  if (currentTs == null || chunks.length === 0) {
    return null;
  }

  const baseTs = toTimestamp(chunks[0].from);
  const relativeMs = currentTs - baseTs;

  if (relativeMs < 0) {
    return null;
  }

  let offsetMs = 0;

  for (let i = 0; i < chunks.length; i++) {
    const d = chunks[i].duration;
    const isLast = i === chunks.length - 1;
    const chunkStart = offsetMs;
    const chunkEnd = offsetMs + d;

    if (isLast) {
      if (relativeMs >= chunkStart && relativeMs <= chunkEnd) {
        return i;
      }
    } else if (relativeMs >= chunkStart && relativeMs < chunkEnd) {
      return i;
    }

    offsetMs += d;
  }

  return null;
}

/**
 * Absolute start/end timestamps for chunk `index` using the same **cumulative duration** model as
 * {@link getActiveChunkIndex}: anchor at `chunks[0].from`, then add prior chunk durations and this
 * chunk's length. Use for seeks when API `chunk.from` / `chunk.to` can disagree with that model.
 */
export function getChunkPlaybackRangeTsFromDurations(
  chunks: FragmentChunk[],
  index: number,
): { startTs: number; stopTs: number } | null {
  if (chunks.length === 0 || index < 0 || index >= chunks.length) {
    return null;
  }

  const baseTs = toTimestamp(chunks[0].from);
  const offsetMs = chunks.slice(0, index).reduce((s, c) => s + c.duration, 0);
  const chunk = chunks[index];
  const startTs = baseTs + offsetMs;
  const stopTs = startTs + chunk.duration;

  return { startTs, stopTs };
}
